// Wire-shape mappers between the **simple** Loyalty Coupon domain type and the
// **legacy** per-POS coupon payloads consumed by the existing backend coupon
// endpoints (`/cova/coupons`, `/greenline/stores/{storeId}/coupons`, etc.).
//
// The legacy shapes were modelled around cannabis-retail concerns (brands,
// excluded products, free-shipping flags, location IDs). The Loyalty surface
// only cares about:  category vs product, discount type, value, status — plus
// the free-shipping flag and participating locations. This module is the bridge.

import type {
  LoyaltyCoupon,
  LoyaltyCouponAppliedToWire,
  LoyaltyCouponAppliesToMode,
  LoyaltyCouponCategoryKey,
  LoyaltyCouponDiscountType,
  LoyaltyCouponDraft,
  LoyaltyCouponFixedTarget,
  LoyaltyCouponLimits,
  LoyaltyCouponProductRef,
  LoyaltyCouponStatus,
  LoyaltyCouponWebCategoryRef,
} from '../../../types/loyaltyCoupon'
import {
  DEFAULT_LOYALTY_COUPON_LIMITS,
  LOYALTY_COUPON_CATEGORY_BG,
  LOYALTY_COUPON_CATEGORY_KEYS,
} from '../../../types/loyaltyCoupon'

const CATEGORY_KEY_SET = new Set<LoyaltyCouponCategoryKey>(
  LOYALTY_COUPON_CATEGORY_KEYS,
)

// ── API → Domain ──────────────────────────────────────────────────────────────

export interface ApiLoyaltyCoupon {
  id?: string | number
  code?: string
  name?: string
  description?: string
  applies_to?: string
  applied_to?: string
  categories?: string[]
  // Web-category refs come back as `{ id, name }` or as a bare ID list.
  web_categories?: Array<{ id: string; name?: string; displayName?: string }>
  // Legacy Cova returns `applied_categories` as a map keyed by category ID.
  applied_categories?: Record<string, { name?: string; displayName?: string }>
  products?: Array<{ id: string; name?: string }> | string[]
  // Cova returns `applied_products` keyed by product ID. Each entry carries
  // its product `type` and (for variable products) a `variations` array of
  // `{ id }` refs. We retain `name` for forward-compat with non-Cova adapters
  // that may surface a display name directly on the map.
  applied_products?: Record<
    string,
    {
      name?: string
      type?: string
      variations?: Array<{ id: string | number }>
    }
  >
  discount_type?: string
  amount?: string | number
  value?: string | number
  // Legacy field name from the Cova coupon API — `'active'` / `'expired'`.
  coupon_status?: string
  status?: string
  redeemed?: number
  redeemed_count?: number
  // Legacy Cova/Woo coupon field — number of times a coupon has been used.
  // This is what the remote API actually populates for the Redeemed column.
  usage_count?: number
  icon?: string
  bg?: string
  date_created?: string
  date_modified?: string
  date_expires?: string | null
  // ── Limit fields (already on the legacy Cova/Woo coupon wire) ────────────
  // Min / max order amount come back as strings (`""` = unset).
  minimum_amount?: string | number | null
  maximum_amount?: string | number | null
  // `usage_limit` = max total uses; `usage_limit_per_user` = per-member cap.
  usage_limit?: number | null
  usage_limit_per_user?: number | null
  // `exclude_sale_items` = "Does not apply to sale items".
  exclude_sale_items?: boolean
  // `individual_use === true` means "cannot be combined" — i.e. NOT stackable.
  individual_use?: boolean
  // Customer eligibility — member emails the coupon is restricted to. Empty /
  // absent means "all customers". Read back so Edit can re-populate the chips.
  email_restrictions?: string[]
  // "Allow free shipping" — the WoocommerceCoupon model stores `free_shipping`
  // as int 0/1; WooCommerce also echoes `allow_free_shipping`.
  free_shipping?: number | boolean
  allow_free_shipping?: number | boolean
  // Participating POS location IDs (Cova / Greenline).
  location_ids?: Array<number | string>
}

const toCategoryKey = (raw: string): LoyaltyCouponCategoryKey | null => {
  return CATEGORY_KEY_SET.has(raw as LoyaltyCouponCategoryKey)
    ? (raw as LoyaltyCouponCategoryKey)
    : null
}

const toDiscountType = (raw?: string): LoyaltyCouponDiscountType => {
  if (raw === 'percent' || raw === 'percentage') return 'percent'
  // Cova back-end stores the legacy WooCommerce code `fixed_product` (and
  // `fixed_cart` for cart-level fixed) — both collapse to `fixed` on the
  // simplified Loyalty surface.
  return 'fixed'
}

const toStatus = (raw?: string): LoyaltyCouponStatus => {
  if (raw === 'expired') return 'expired'
  return 'active'
}

// Recover the whole-cart vs per-item basis from the wire `discount_type`.
// `fixed_cart` is the only value that encodes a single amount off the whole
// cart; every other wire value (`fixed_product`, `percent`) is per-item, so it
// maps to `'item'`. This lets Edit re-open a saved `fixed_cart` coupon on the
// "Cart total" option.
const toFixedTarget = (raw?: string): LoyaltyCouponFixedTarget => {
  return raw === 'fixed_cart' ? 'cart' : 'item'
}

// Full wire enum:
//   class AppliedTo:
//     SPECIFIC_PRODUCTS    = "specific_products"
//     SPECIFIC_BRANDS      = "specific_brands"
//     SPECIFIC_CATEGORIES  = "specific_categories"
//     CART_TOTAL           = "cart_total"
//     ALL_PRODUCTS         = "all_products"
const APPLIED_TO_WIRE_VALUES = new Set<LoyaltyCouponAppliedToWire>([
  'all_products',
  'specific_categories',
  'specific_products',
  'specific_brands',
  'cart_total',
])

// Captures the literal wire value when it matches the closed enum — used to
// drive the list/grid's "Applies to" display label so legacy `specific_brands`
// / `cart_total` coupons render correctly even though the modal can't edit them.
const toAppliedToWire = (
  raw: string | undefined,
): LoyaltyCouponAppliedToWire | undefined => {
  if (!raw) return undefined
  return APPLIED_TO_WIRE_VALUES.has(raw as LoyaltyCouponAppliedToWire)
    ? (raw as LoyaltyCouponAppliedToWire)
    : undefined
}

const toAppliesToMode = (
  raw: string | undefined,
  hasProducts: boolean,
): LoyaltyCouponAppliesToMode => {
  // Closed wire enum first — covers the full Cova/Woo `AppliedTo` set.
  // `specific_brands` and `cart_total` aren't editable as distinct tabs, so
  // they collapse to `'all'` here (the "All Products" tab); the original value
  // is preserved separately on `appliedToRaw` for list/grid display. A
  // `fixed_cart` coupon is stored with `applied_to: 'cart_total'`, which
  // collapses to `'all'` here — so it round-trips back onto the All Products
  // tab with Fixed selected (and `fixedTarget: 'cart'` via `toFixedTarget`).
  if (raw === 'all_products' || raw === 'cart_total') return 'all'
  if (raw === 'specific_products') return 'products'
  if (raw === 'specific_categories' || raw === 'specific_brands') {
    return 'categories'
  }
  // Domain-shape pass-throughs (e.g. when the mapper round-trips a draft).
  if (raw === 'all') return 'all'
  if (raw === 'products') return 'products'
  if (raw === 'categories') return 'categories'
  // Legacy / malformed payloads — best effort. We can't infer "all" here, so
  // bias toward the explicit modes.
  return hasProducts ? 'products' : 'categories'
}

// ── Domain → wire enum helper ─────────────────────────────────────────────────
//
// The client model splits the backend `discount_type` into two axes —
// `appliesTo` (scope) and `discountType` (fixed vs percent). This helper
// recombines them into the `applied_to` + `discount_type` pair the
// Cova/Greenline (WoocommerceCoupon) serializer expects. Kept here as the one
// source of truth so Cova, Greenline, and the generic path can't drift apart.

// `applied_to` and `discount_type` are computed together because they travel as
// a pair on the wire — in particular `fixed_cart` is ONLY ever emitted
// alongside `applied_to: 'cart_total'`. The mapping (the whole-store fixed row
// now branches on the merchant's explicit `fixedTarget` choice):
//
//   Applies-to scope   Fixed / fixedTarget                Percent
//   ────────────────   ────────────────────────────────   ─────────────────────────
//   'all' (store)      cart → fixed_cart + cart_total      percent + all_products
//                      item → fixed_product + all_products
//   'categories'       fixed_product + specific_categories   percent + specific_categories
//   'products'         fixed_product + specific_products     percent + specific_products
//
// `specific_brands` is intentionally never emitted — the modal can't select it.
interface WireDiscount {
  applied_to:
    | 'cart_total'
    | 'all_products'
    | 'specific_categories'
    | 'specific_products'
  discount_type: 'percent' | 'fixed_cart' | 'fixed_product'
}

const toWireDiscount = (
  discountType: LoyaltyCouponDiscountType,
  mode: LoyaltyCouponAppliesToMode,
  fixedTarget: LoyaltyCouponFixedTarget,
): WireDiscount => {
  const isPercent = discountType === 'percent'
  if (mode === 'products') {
    return {
      applied_to: 'specific_products',
      discount_type: isPercent ? 'percent' : 'fixed_product',
    }
  }
  if (mode === 'categories') {
    return {
      applied_to: 'specific_categories',
      discount_type: isPercent ? 'percent' : 'fixed_product',
    }
  }
  // Whole-store scope. A percent discount stays a storewide percentage. A fixed
  // discount now branches on the merchant's explicit `fixedTarget`: 'cart'
  // collapses to a single cart-total deal (fixed_cart + cart_total — they must
  // travel together), 'item' takes the amount off each item store-wide
  // (fixed_product + all_products).
  if (isPercent) {
    return { applied_to: 'all_products', discount_type: 'percent' }
  }
  return fixedTarget === 'cart'
    ? { applied_to: 'cart_total', discount_type: 'fixed_cart' }
    : { applied_to: 'all_products', discount_type: 'fixed_product' }
}

const toProductRefs = (
  raw: ApiLoyaltyCoupon['products'],
): LoyaltyCouponProductRef[] => {
  if (!raw) return []
  return raw.map((item) =>
    typeof item === 'string'
      ? { id: item, name: item }
      : { id: String(item.id), name: item.name ?? String(item.id) },
  )
}

const toWebCategoryRefs = (
  raw: ApiLoyaltyCoupon['web_categories'],
): LoyaltyCouponWebCategoryRef[] => {
  if (!raw) return []
  return raw.map((item) => ({
    id: String(item.id),
    displayName: item.displayName ?? item.name ?? String(item.id),
  }))
}

const toWebCategoryRefsFromAppliedMap = (
  raw: ApiLoyaltyCoupon['applied_categories'],
): LoyaltyCouponWebCategoryRef[] => {
  if (!raw) return []
  return Object.entries(raw).map(([id, item]) => ({
    id,
    displayName: item?.displayName ?? item?.name ?? id,
  }))
}

// Coerce the wire's free-string `type` to the closed `'simple' | 'variable'`
// union. Anything we don't recognise falls back to `'variable'` so the
// outbound Cova payload always sends a non-empty `variations` array path.
const toProductType = (raw: unknown): 'simple' | 'variable' => {
  return raw === 'simple' ? 'simple' : 'variable'
}

const toProductRefsFromAppliedMap = (
  raw: ApiLoyaltyCoupon['applied_products'],
): LoyaltyCouponProductRef[] => {
  if (!raw) return []
  return Object.entries(raw).map(([id, item]) => ({
    id,
    name: item?.name ?? id,
    type: toProductType(item?.type),
    variations: (item?.variations ?? []).map((v) => ({ id: String(v.id) })),
  }))
}

const toValue = (raw: string | number | undefined): number => {
  if (raw === undefined || raw === null || raw === '') return 0
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw)
  return Number.isFinite(n) ? n : 0
}

// Coerce a wire value to either a finite number or `null` (unlimited / unset).
const toNullableNumber = (
  raw: string | number | null | undefined,
): number | null => {
  if (raw === null || raw === undefined || raw === '') return null
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw)
  return Number.isFinite(n) ? n : null
}

// Extract the limits sub-object from a raw API coupon. Backend-supported
// fields are read directly; not-yet-supported fields fall back to the
// defaults defined on the domain type.
const toLimits = (raw: ApiLoyaltyCoupon): LoyaltyCouponLimits => ({
  ...DEFAULT_LOYALTY_COUPON_LIMITS,
  expiresAt: raw.date_expires ?? null,
  perPerson: toNullableNumber(raw.usage_limit_per_user),
  total: toNullableNumber(raw.usage_limit),
  minOrder: toNullableNumber(raw.minimum_amount),
  maxDiscount: toNullableNumber(raw.maximum_amount),
  excludeSale:
    raw.exclude_sale_items ?? DEFAULT_LOYALTY_COUPON_LIMITS.excludeSale,
  // `stackable` is the inverse of `individual_use`. If the backend omits
  // `individual_use` we fall back to "not stackable" (matches the HTML
  // default — line 1162 ships with the toggle off).
  stackable:
    raw.individual_use !== undefined
      ? !raw.individual_use
      : DEFAULT_LOYALTY_COUPON_LIMITS.stackable,
})

export const mapApiCouponToDomain = (raw: ApiLoyaltyCoupon): LoyaltyCoupon => {
  const categories = (raw.categories ?? [])
    .map(toCategoryKey)
    .filter((c): c is LoyaltyCouponCategoryKey => c !== null)
  // Prefer the legacy Cova `applied_products` map if present, otherwise the
  // simplified `products` list. Same fallback for categories.
  const products = raw.applied_products
    ? toProductRefsFromAppliedMap(raw.applied_products)
    : toProductRefs(raw.products)
  const webCategories = raw.web_categories
    ? toWebCategoryRefs(raw.web_categories)
    : toWebCategoryRefsFromAppliedMap(raw.applied_categories)
  const primaryCategory = categories[0]
  const appliedToWire = toAppliedToWire(raw.applied_to ?? raw.applies_to)

  return {
    id: String(raw.id ?? ''),
    code: (raw.code ?? '').toString(),
    name: raw.name ?? '',
    description: raw.description ?? '',
    appliesTo: toAppliesToMode(
      raw.applied_to ?? raw.applies_to,
      products.length > 0,
    ),
    appliedToRaw: appliedToWire,
    categories,
    webCategories,
    products,
    discountType: toDiscountType(raw.discount_type),
    // `fixed_cart` → 'cart' (whole-cart deal); anything else → 'item'. Lets
    // Edit re-open a saved cart-total coupon on the "Cart total" option.
    fixedTarget: toFixedTarget(raw.discount_type),
    value: toValue(raw.value ?? raw.amount),
    status: toStatus(raw.coupon_status ?? raw.status),
    // Prefer the legacy wire field `usage_count` — that is what the remote
    // coupon list actually populates. Fall back to the speculative
    // `redeemed_count` / `redeemed` shapes for forward compatibility.
    redeemedCount: raw.usage_count ?? raw.redeemed_count ?? raw.redeemed ?? 0,
    dateExpires: raw.date_expires ?? undefined,
    limits: toLimits(raw),
    // Pass through whatever the API returns as-is (may be `''`/undefined) —
    // the default ticket glyph is applied at render time, not here.
    icon: raw.icon || undefined,
    emailRestrictions: Array.isArray(raw.email_restrictions)
      ? raw.email_restrictions
      : [],
    freeShipping: Boolean(raw.free_shipping ?? raw.allow_free_shipping),
    locationIds: (raw.location_ids ?? [])
      .map(Number)
      .filter((n) => Number.isFinite(n)),
    bg:
      raw.bg ??
      (primaryCategory
        ? LOYALTY_COUPON_CATEGORY_BG[primaryCategory]
        : '#F0FDF4'),
    createdAt: raw.date_created,
    updatedAt: raw.date_modified,
  }
}

// ── Domain → API ──────────────────────────────────────────────────────────────

// The Cova coupon endpoint expects expiry as `YYYY-MM-DDTHH:mm:ss` (seconds
// required — `2026-05-20T01:15:00`). The modal's `<input type="datetime-local">`
// produces `YYYY-MM-DDTHH:mm` (no seconds), so we append `:00` here. We also
// accept already-seconded input (e.g. when round-tripping an edited coupon) and
// pass it through untouched.
const normalizeDateExpiresForApi = (raw: string | undefined): string | null => {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  // Drop any timezone suffix the input might carry (`Z` / `±HH:mm`).
  const noZone = trimmed.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')
  // Already includes seconds (HH:mm:ss)?
  if (/T\d{2}:\d{2}:\d{2}/.test(noZone)) return noZone
  // Otherwise append `:00`.
  return `${noZone}:00`
}

// Companion to `normalizeDateExpiresForApi`. The backend derives a coupon's
// status from a stored UTC field `date_expires_gmt`, string-compared against a
// value it produces via `strftime("%Y-%m-%dT%H:%M:%S")` in UTC — exactly 19
// chars, NO trailing `Z`, NO milliseconds, NO offset. We compute that UTC value
// on the client (which knows the admin's timezone) from the SAME normalized
// local wall-clock string the mappers already build, and send it alongside the
// still-local `date_expires`. A cleared/disabled expiry maps to `null`, exactly
// like `date_expires` does.
const normalizeDateExpiresGmtForApi = (
  raw: string | undefined,
): string | null => {
  const local = normalizeDateExpiresForApi(raw)
  if (!local) return null
  // `new Date("2030-12-31T00:00:00")` parses the offset-less string as
  // browser-local; `.toISOString()` re-expresses it in UTC; `.slice(0, 19)`
  // drops the trailing `.000Z` so the format matches the backend byte-for-byte.
  const utc = new Date(local)
  if (Number.isNaN(utc.getTime())) return null
  return utc.toISOString().slice(0, 19)
}

export interface ApiLoyaltyCouponDraft {
  code: string
  name: string
  description: string
  // User-selected emoji, or `''` when unset — the default ticket glyph is
  // applied client-side at render time, never written to the wire.
  icon: string
  // Domain Applies-to mode passed straight through. The Cova/Greenline
  // adapters translate this into the backend `applied_to` enum
  // (`cart_total` / `all_products` / `specific_*`) via `toWireDiscount`; other
  // adapters can pass the literal through if/when their back-ends accept it.
  applies_to: 'all' | 'categories' | 'products'
  web_categories: string[]
  products: string[]
  // Derived from the Applies-to scope + fixed/percent toggle + `fixedTarget`
  // so every adapter agrees (see `toWireDiscount`): 'all' + fixed →
  // `fixed_cart` (cart) or `fixed_product` (item), categories/products + fixed
  // → `fixed_product`, percent → `percent`.
  discount_type: 'percent' | 'fixed_cart' | 'fixed_product'
  amount: string
  date_expires: string | null
  // UTC mirror of `date_expires` (`YYYY-MM-DDTHH:mm:ss`, no `Z`/ms/offset) that
  // the backend persists and uses to derive coupon status. `null` when unset.
  date_expires_gmt: string | null
  // ── Limit fields supported by the legacy Cova/Woo coupon wire ────────────
  minimum_amount: string
  maximum_amount: string
  usage_limit: number | null
  usage_limit_per_user: number | null
  exclude_sale_items: boolean
  // `individual_use === true` means "cannot stack" — invert `limits.stackable`.
  individual_use: boolean
  // Customer eligibility — member emails the coupon is restricted to. Empty
  // array = "all customers". Covers flowhub/lightspeed/shopify/woocommerce via
  // this generic mapper (Cova/Greenline have their own outbound builders).
  email_restrictions: string[]
  // ── Not yet on the backend; commented out until the coupon schema ships
  //    them. Re-enable here (and in `mapDraftToCovaApi`) in lockstep with the
  //    backend agent.
  // expiry_notify_days?: number | null
  // usage_limit_per_day?: number | null
  // frequency?: 'any' | 'day' | 'week' | 'month'
  // exclude_gift_cards?: boolean
  // stack_with_member_pricing?: boolean
  // in_store_only?: boolean
  // first_order_only?: boolean
}

// Pull the wire expiry from the draft, preferring `limits.expiresAt` over the
// legacy top-level `dateExpires` (kept for back-compat with callers that
// haven't migrated yet).
const draftExpiry = (draft: LoyaltyCouponDraft): string | undefined => {
  return draft.limits?.expiresAt ?? draft.dateExpires ?? undefined
}

// Empty string for `null` numeric — the legacy Cova/Woo coupon wire uses `""`
// (not `null`) as the "unset" sentinel for `minimum_amount` / `maximum_amount`.
const toMoneyStr = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n)

export const mapDraftToApi = (
  draft: LoyaltyCouponDraft,
): ApiLoyaltyCouponDraft => {
  const limits = draft.limits ?? DEFAULT_LOYALTY_COUPON_LIMITS
  return {
    code: draft.code.trim().toUpperCase().replace(/\s+/g, '_'),
    name: draft.name.trim(),
    description: draft.description.trim(),
    icon: draft.icon?.trim() ?? '',
    applies_to: draft.appliesTo,
    web_categories: draft.webCategories.map((w) => w.id),
    products: draft.products.map((p) => p.id),
    // Derived so Cova, Greenline, and this generic path all agree — see
    // `toWireDiscount` ('all' + fixed branches on `fixedTarget`, etc.). The
    // generic shape only carries the domain `applies_to`, so it can't express
    // `cart_total`.
    discount_type: toWireDiscount(
      draft.discountType,
      draft.appliesTo,
      draft.fixedTarget,
    ).discount_type,
    amount: String(draft.value),
    date_expires: normalizeDateExpiresForApi(draftExpiry(draft)),
    date_expires_gmt: normalizeDateExpiresGmtForApi(draftExpiry(draft)),
    minimum_amount: toMoneyStr(limits.minOrder),
    maximum_amount: toMoneyStr(limits.maxDiscount),
    usage_limit: limits.total,
    usage_limit_per_user: limits.perPerson,
    exclude_sale_items: limits.excludeSale,
    individual_use: !limits.stackable,
    email_restrictions: draft.emailRestrictions ?? [],
  }
}

// Shared by `mapDraftToGreenlineApi` and `mapDraftToCovaApi` below — both
// endpoints are backed by the identical `WoocommerceCoupon` model/serializer
// pair (`backend/be_libs/models/woocommerce_coupon.py`, confirmed byte-
// identical `CreateSerializer`s in `serializers/greenline/coupons.py` and
// `crm_be_cova_api/serializers/coupons.py`), so they share the same
// `applied_categories`/`applied_products` wire shape.

// `{"<category_id>": {"locations": [{"id": <locationId>}, ...]}}` — every
// selected category is scoped to the coupon's participating location IDs (see
// `resolveLocationIds`).
const buildLocationScopedMap = (
  ids: string[],
  locationIds: number[],
): Record<string, { locations: Array<{ id: number }> }> => {
  const locs = locationIds.map((id) => ({ id }))
  return Object.fromEntries(ids.map((cid) => [cid, { locations: locs }]))
}

// The merchant's "Participating Locations" selection wins; an empty / absent
// selection falls back to every store location (the pre-picker behaviour), so
// the backend's required `location_ids` is never sent empty.
const resolveLocationIds = (
  draft: LoyaltyCouponDraft,
  fallback: number[],
): number[] => (draft.locationIds?.length ? draft.locationIds : fallback)

// `{"<product_id>": {"type": "simple"|"variable", "variations": [{"id": ...}]}}`.
// Both fields are required by the backend's nested `ProductItem` serializer
// (no defaults) — even a `simple` product must send `variations: []`. We
// default `type` to `'variable'` and `variations` to `[]` when the picker
// hasn't populated them (which can happen for legacy data; the user will be
// asked to re-pick the product on next edit).
const buildAppliedProductsMap = (
  products: LoyaltyCouponProductRef[],
): Record<
  string,
  { type: 'simple' | 'variable'; variations: Array<{ id: string }> }
> => {
  return Object.fromEntries(
    products.map((p) => [
      p.id,
      {
        type: p.type ?? 'variable',
        variations: (p.variations ?? []).map((v) => ({ id: v.id })),
      },
    ]),
  )
}

// ── Domain → Greenline API (legacy `/greenline/stores/{id}/coupons` payload) ──
//
// Confirmed via a live 400 response (ACD-15035): the Greenline coupon endpoint
// rejects the plain `mapDraftToApi` payload with
// `{"location_ids":["This field is required."],"applied_to":["This field is
// required."]}`. Both fields descend from the same shared legacy mapper the
// pre-migration app used for Cova AND Greenline coupons
// (`frontend/src/pages/Coupon/hooks/mapper.js#mapDataToApi`) — Cova's adapter
// already carries the full legacy shape via `mapDraftToCovaApi` below;
// Greenline only needed these two additions on top of the simplified wire.
//
// `discount_type` also needs remapping (second ACD-15035 round): the
// Greenline serializer's `discount_type` is a `ChoiceField` on
// `WoocommerceCoupon.DiscountType` (`backend/be_libs/models/woocommerce_coupon.py`)
// — valid values are `'percent'`, `'fixed_cart'`, `'fixed_product'`; the
// simplified domain value `'fixed'` isn't one of them. Cova's coupon endpoint
// shares the exact same serializer/model, so this uses the shared
// `toWireDiscount` helper `mapDraftToCovaApi` also uses. `'fixed_cart'` is
// emitted for the whole-store fixed case ('all' + fixed) and is paired with
// `applied_to: 'cart_total'` (the two travel together — a single amount off
// the cart).
//
// Third round: the simplified `web_categories`/`products` arrays aren't read
// by the Greenline serializer/view at all (confirmed by grep — zero hits).
// `applied_to: specific_categories`/`specific_products` instead require the
// legacy `applied_categories`/`applied_products` maps to be non-empty
// (`serializers/greenline/coupons.py#validate`, `BadRequestException` when
// the matching map is missing/empty) — so `web_categories`/`products` are
// dropped from the outbound payload and replaced with the maps below.

export interface ApiGreenlineCouponDraft
  extends Omit<
    ApiLoyaltyCouponDraft,
    'discount_type' | 'web_categories' | 'products'
  > {
  applied_to:
    | 'cart_total'
    | 'all_products'
    | 'specific_categories'
    | 'specific_products'
  location_ids: number[]
  discount_type: 'percent' | 'fixed_product' | 'fixed_cart'
  applied_categories: Record<string, { locations: Array<{ id: number }> }>
  applied_products: Record<
    string,
    { type: 'simple' | 'variable'; variations: Array<{ id: string }> }
  >
  applied_brands: Record<string, unknown>
  excluded_categories: Record<string, unknown>
  excluded_products: Record<string, unknown>
  excluded_brands: Record<string, unknown>
  free_shipping: 0 | 1
}

export const mapDraftToGreenlineApi = (
  draft: LoyaltyCouponDraft,
  locationIds: number[],
): ApiGreenlineCouponDraft => {
  // `web_categories`/`products` (and the generic `discount_type`) are dropped —
  // see the comment above; the pair is recomputed via `toWireDiscount` below.
  const { web_categories, products, discount_type, ...base } =
    mapDraftToApi(draft)
  const categoryIds = draft.webCategories.map((w) => w.id)
  const selectedLocationIds = resolveLocationIds(draft, locationIds)

  // Shared `applied_to` + `discount_type` pair (see `toWireDiscount`) — Cova and
  // Greenline share the same `AppliedTo` / `DiscountType` enums (WoocommerceCoupon
  // model). `fixed_cart` travels with `cart_total` for the whole-store fixed case.
  const { applied_to, discount_type: wireDiscountType } = toWireDiscount(
    draft.discountType,
    draft.appliesTo,
    draft.fixedTarget,
  )

  return {
    ...base,
    applied_to,
    location_ids: selectedLocationIds,
    discount_type: wireDiscountType,
    applied_categories:
      applied_to === 'specific_categories'
        ? buildLocationScopedMap(categoryIds, selectedLocationIds)
        : {},
    applied_products:
      applied_to === 'specific_products'
        ? buildAppliedProductsMap(draft.products)
        : {},
    applied_brands: {},
    excluded_categories: {},
    excluded_products: {},
    excluded_brands: {},
    free_shipping: draft.freeShipping ? 1 : 0,
  }
}

// ── Domain → WooCommerce API ────────────────────────────────────────────────
//
// The generic payload plus the free-shipping pair the legacy Woo form sends
// (`frontend/src/pages/Coupon/Form/index.js` — both keys carry the same 0/1).
// Kept off `mapDraftToApi` so Flowhub / Lightspeed / Shopify payloads stay
// unchanged.

export interface ApiWooCouponDraft extends ApiLoyaltyCouponDraft {
  free_shipping: 0 | 1
  allow_free_shipping: 0 | 1
}

export const mapDraftToWooApi = (
  draft: LoyaltyCouponDraft,
): ApiWooCouponDraft => {
  const freeShipping = draft.freeShipping ? 1 : 0
  return {
    ...mapDraftToApi(draft),
    free_shipping: freeShipping,
    allow_free_shipping: freeShipping,
  }
}

// ── Domain → Cova API (legacy `/cova/coupons` payload) ──────────────────────
//
// Cova's coupon endpoint expects the historical cannabis-retail shape with
// per-location `applied_categories` / `applied_products` maps. We synthesise
// it from the simplified draft here so the rest of the UI never has to know
// about the legacy fields. Mirrors the legacy
// `frontend/src/pages/Coupon/hooks/mapper.js#mapDataToApi` flow.

export interface ApiCovaCouponDraft {
  organization_id: string
  store_id: string
  code: string
  name: string
  description: string
  icon: string
  amount: string
  // Cova/WooCommerce-style wire values — `fixed_product` for per-scope $-off,
  // `percent` for %-off, `fixed_cart` for a single amount off the cart total.
  // `fixed_cart` is emitted for the whole-store fixed case and always paired
  // with `applied_to: 'cart_total'` (they travel together).
  discount_type: 'fixed_product' | 'percent' | 'fixed_cart'
  applied_to:
    | 'cart_total'
    | 'all_products'
    | 'specific_categories'
    | 'specific_products'
  excluded_to: 'no_exclusions' | 'specific_categories' | 'specific_products'
  // Location IDs inside `locations` are numeric (must mirror the top-level
  // `location_ids` array which the back-end indexes by integer).
  applied_categories: Record<string, { locations: Array<{ id: number }> }>
  // `applied_products` does NOT share the categories' location-scoped shape.
  // Cova expects per-product `type` + `variations` — mirrors the legacy
  // `frontend/src/pages/Coupon/hooks/mapper.js#revertProductsToApi` output:
  //   { '3636491': { type: 'variable', variations: [{ id: '3636493' }, …] } }
  applied_products: Record<
    string,
    { type: 'simple' | 'variable'; variations: Array<{ id: string }> }
  >
  applied_brands: Record<string, unknown>
  excluded_categories: Record<string, unknown>
  excluded_products: Record<string, unknown>
  excluded_brands: Record<string, unknown>
  free_shipping: 0 | 1
  minimum_amount: string
  maximum_amount: string
  individual_use: boolean
  exclude_sale_items: boolean
  usage_limit: number | null
  usage_limit_per_user: number | null
  limit_usage_to_x_items: number | null
  email_restrictions: string[]
  date_expires: string | null
  // UTC mirror of `date_expires` (`YYYY-MM-DDTHH:mm:ss`, no `Z`/ms/offset) that
  // the backend persists and uses to derive coupon status. `null` when unset.
  date_expires_gmt: string | null
  location_ids: number[]
}

export interface CovaCouponContext {
  organizationId: string
  storeId: string
  locationIds: number[]
}

// `buildLocationScopedMap` / `buildAppliedProductsMap` are defined above
// (shared with `mapDraftToGreenlineApi` — same wire shape, same backend
// model).

export const mapDraftToCovaApi = (
  draft: LoyaltyCouponDraft,
  ctx: CovaCouponContext,
): ApiCovaCouponDraft => {
  const categoryIds = draft.webCategories.map((w) => w.id)
  const limits = draft.limits ?? DEFAULT_LOYALTY_COUPON_LIMITS
  const locationIds = resolveLocationIds(draft, ctx.locationIds)

  // Shared `applied_to` + `discount_type` pair (see `toWireDiscount`). The
  // whole-store fixed case emits `fixed_cart` + `cart_total` together; the
  // whole-store percent case stays `percent` + `all_products`.
  const { applied_to, discount_type } = toWireDiscount(
    draft.discountType,
    draft.appliesTo,
    draft.fixedTarget,
  )

  return {
    organization_id: ctx.organizationId,
    store_id: ctx.storeId,
    code: draft.code.trim().toUpperCase().replace(/\s+/g, '_'),
    name: draft.name.trim(),
    description: draft.description.trim(),
    icon: draft.icon?.trim() ?? '',
    amount: String(draft.value),
    discount_type,
    applied_to,
    excluded_to: 'specific_products',
    applied_categories:
      applied_to === 'specific_categories'
        ? buildLocationScopedMap(categoryIds, locationIds)
        : {},
    applied_products:
      applied_to === 'specific_products'
        ? buildAppliedProductsMap(draft.products)
        : {},
    applied_brands: {},
    excluded_categories: {},
    excluded_products: {},
    excluded_brands: {},
    free_shipping: draft.freeShipping ? 1 : 0,
    // ── Backend-supported limit fields ──────────────────────────────────────
    minimum_amount: toMoneyStr(limits.minOrder),
    maximum_amount: toMoneyStr(limits.maxDiscount),
    individual_use: !limits.stackable,
    exclude_sale_items: limits.excludeSale,
    usage_limit: limits.total,
    usage_limit_per_user: limits.perPerson,
    // `limit_usage_to_x_items` is a separate cannabis-retail concept (cap on
    // how many items the discount applies to per order); not surfaced in the
    // new Limitations & Restrictions UI.
    limit_usage_to_x_items: null,
    // Customer eligibility — member emails the coupon is restricted to. Empty
    // array = "all customers". Previously hard-coded `[]`; now carries the
    // draft's selection through to the Cova coupon endpoint.
    email_restrictions: draft.emailRestrictions ?? [],
    date_expires: normalizeDateExpiresForApi(draftExpiry(draft)),
    date_expires_gmt: normalizeDateExpiresGmtForApi(draftExpiry(draft)),
    location_ids: locationIds,
    // ── Not yet on the Cova wire — re-enable in lockstep with backend ──────
    // usage_limit_per_day: limits.perDay,
    // frequency: limits.frequency,
    // expiry_notify_days: limits.expiryNotifyDays,
    // exclude_gift_cards: limits.excludeGiftCards,
    // stack_with_member_pricing: limits.stackMember,
    // in_store_only: limits.inStoreOnly,
    // first_order_only: limits.firstOrderOnly,
  }
}
